/**
 * ★ "OUR STANDARDS AND AGREEMENT" - THE APPLICATION'S LAST STEP (design 25; design 24, T24, the
 * owner's E1 and E2: three standards checkboxes plus one for the author agreement).
 *
 * Reached from the header's "Submit for review" while a submission document is owed
 * (../domain/agreements.ts). It asks for each owed document, ONE BOX PER RECORDED CONSENT, NEVER
 * PRE-TICKED (design 25, section 6): the three editorial standards while they have a version in
 * force, each to be ticked, and "I accept the author agreement" - each document linked in a new tab,
 * because its text lives on moonx.app and is never drawn here (G28; ./TermsGate.tsx says why).
 * Where only the author agreement is in force (a fresh database), only its box shows.
 *
 * ITS "Submit for review" IS THE HEADER'S OWN ACTION. With every box ticked it hands back to the
 * page, which records the documents in one `acceptAgreements` call and then submits
 * (../data/useApplicationActions.ts). With one left, it sends nothing, says what to do and moves
 * focus to the first box still empty - the form is checked when the person presses, not before.
 *
 * Drawn as the author agreement's own page is (./TermsGate.tsx): a reading column with the page's
 * one `<h1>`, and each document in a calm card.
 */
import { Anchor, Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import { humanMessage } from '../api/problem';
import { ErrorState, LoadingState } from '../components/States';
import classes from '../components/profile.module.css';
import {
  AUTHOR_AGREEMENT_CODE,
  EDITORIAL_STANDARDS,
  EDITORIAL_STANDARDS_CODE,
} from '../domain/agreements';
import type { SubmissionDocument } from '../domain/agreements';

export interface AgreementStepProps {
  /** The owed submission documents, or `undefined` while there is nothing to tell from yet. */
  readonly documents: readonly SubmissionDocument[] | undefined;
  /** Why the documents could not be told, when they could not. */
  readonly loadError: unknown;
  readonly onRetry: () => void;
  /** "Submit for review" or "Resubmit for review": the header's own action. */
  readonly submitLabel: string;
  /** Called with every box ticked. */
  readonly onSubmit: () => void;
  readonly busy: boolean;
  readonly submitError: unknown;
  readonly onBack: () => void;
}

/** One box: its id, and the words beside it. */
interface Box {
  readonly id: string;
  readonly label: string;
}

/** The boxes a document asks for: one per standard, or one for the whole document. */
function boxesOf(owed: SubmissionDocument, t: TFunction<'application'>): readonly Box[] {
  const key = `agreement-${owed.documentCode}-${owed.versionLabel}`;
  if (owed.documentCode === EDITORIAL_STANDARDS_CODE) {
    return EDITORIAL_STANDARDS.map((label, index) => ({
      id: `${key}-${String(index)}`,
      label: t(label),
    }));
  }
  if (owed.documentCode === AUTHOR_AGREEMENT_CODE) {
    return [{ id: key, label: t('agreement.acceptAuthorAgreement') }];
  }
  return [{ id: key, label: t('agreement.acceptDocument', { title: owed.title }) }];
}

/** How the document's own link reads. */
function linkWords(owed: SubmissionDocument, t: TFunction<'application'>): string {
  if (owed.documentCode === EDITORIAL_STANDARDS_CODE) {
    return t('agreement.readStandards');
  }
  if (owed.documentCode === AUTHOR_AGREEMENT_CODE) {
    return t('agreement.readAuthorAgreement');
  }
  return t('agreement.readDocument', { title: owed.title });
}

export function AgreementStep({
  documents,
  loadError,
  onRetry,
  submitLabel,
  onSubmit,
  busy,
  submitError,
  onBack,
}: AgreementStepProps): ReactNode {
  const { t } = useTranslation('application');
  const [ticked, setTicked] = useState<Readonly<Record<string, boolean>>>({});
  const [refused, setRefused] = useState(false);

  if (documents === undefined) {
    return (
      <div className={classes.termsPage}>
        {loadError === null || loadError === undefined ? (
          <LoadingState label={t('agreement.loading')} lines={4} />
        ) : (
          <ErrorState error={loadError} fallback={t('agreement.loadFailed')} onRetry={onRetry} />
        )}
      </div>
    );
  }

  const boxes = documents.flatMap((owed) => boxesOf(owed, t));
  const firstEmpty = boxes.find((box) => ticked[box.id] !== true);

  const press = (): void => {
    if (firstEmpty !== undefined) {
      setRefused(true);
      document.getElementById(firstEmpty.id)?.focus();
      return;
    }
    setRefused(false);
    onSubmit();
  };

  return (
    <div className={classes.termsPage}>
      <Stack gap="lg">
        <div>
          <h1 className={classes.pageTitle}>{t('agreement.title')}</h1>
          <p className={classes.lede}>
            {documents.length === 0 ? t('agreement.allAccepted') : t('agreement.lede')}
          </p>
        </div>

        {documents.map((owed) => (
          <div key={`${owed.documentCode}-${owed.versionLabel}`} className={classes.plainCard}>
            <Stack gap="sm">
              {owed.documentCode === EDITORIAL_STANDARDS_CODE ? (
                <Stack gap={4}>
                  <Text fw={700}>{t('agreement.standards.title')}</Text>
                  <Text size="sm" c="dimmed">
                    {t('agreement.standards.intro')}
                  </Text>
                </Stack>
              ) : null}
              {boxesOf(owed, t).map((box) => (
                <Checkbox
                  key={box.id}
                  id={box.id}
                  label={box.label}
                  checked={ticked[box.id] === true}
                  disabled={busy}
                  onChange={(event) => {
                    const next = event.currentTarget.checked;
                    setTicked((current) => ({ ...current, [box.id]: next }));
                  }}
                />
              ))}
              <Anchor href={owed.contentUrl} target="_blank" rel="noopener noreferrer" size="sm">
                {linkWords(owed, t)}
              </Anchor>
            </Stack>
          </div>
        ))}

        {documents.length > 0 ? (
          <Text size="xs" c="dimmed">
            {t('agreement.records')}
          </Text>
        ) : null}

        {documents.length > 0 ? (
          <Text size="xs" c="dimmed">
            {t('agreement.tipsNote')}
          </Text>
        ) : null}

        {refused && firstEmpty !== undefined ? (
          <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
            {t('agreement.tickEveryBox')}
          </Text>
        ) : null}
        {submitError !== null && submitError !== undefined ? (
          <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
            {humanMessage(submitError, t('agreement.submitFailed'), undefined, t)}
          </Text>
        ) : null}

        <Group>
          <Button loading={busy} onClick={press}>
            {submitLabel}
          </Button>
          <Button variant="subtle" onClick={onBack} disabled={busy}>
            {t('agreement.back')}
          </Button>
        </Group>
      </Stack>
    </div>
  );
}
